import type { BillingPlanId } from "@quire/config";

export type Platform = "web" | "ios" | "android" | "desktop";

export type LocalSession = {
  userId: string;
  email: string;
  name: string;
  emailVerified: boolean;
  image?: string | null;
  orgId: string;
  workspaceId?: string;
  workspaceName?: string;
  role?: "owner" | "admin" | "member";
  currentProjectId?: string;
  currentProjectPlatform?: Platform;
  plan?: BillingPlanId;
  effectivePlan?: BillingPlanId;
  isActive?: boolean;
  isReadOnly?: boolean;
  canUsePaidMessaging?: boolean;
  status?: "inactive" | "incomplete" | "incomplete_expired" | "trialing" | "active" | "past_due" | "canceled" | "unpaid" | "revoked" | "expired";
  accessLevel?: "full" | "read_only" | "blocked";
  trialEndsAt?: string | null;
  trialState?: "active" | "expired" | "no_trial" | "none";
  onboardingComplete?: boolean;
  onboardingStep?: number;
};

const SESSION_KEY = "Quire_session";
export const SESSION_CHANGED_EVENT = "quire:session-changed";
let inMemorySession: LocalSession | null = null;

function emitSessionChanged(): void {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new Event(SESSION_CHANGED_EVENT));
}

function sanitizeSession(input: unknown): LocalSession | null {
  if (!input || typeof input !== "object") {
    return null;
  }

  const s = input as Record<string, unknown>;
  if (typeof s.userId !== "string" || typeof s.email !== "string" || typeof s.orgId !== "string") {
    return null;
  }

  return {
    userId: s.userId,
    email: s.email,
    name: typeof s.name === "string" ? s.name : "",
    emailVerified: Boolean(s.emailVerified),
    image: typeof s.image === "string" || s.image === null ? s.image : undefined,
    orgId: s.orgId,
    workspaceId: typeof s.workspaceId === "string" ? s.workspaceId : undefined,
    workspaceName: typeof s.workspaceName === "string" ? s.workspaceName : undefined,
    role: s.role === "owner" || s.role === "admin" || s.role === "member" ? s.role : undefined,
    currentProjectId: typeof s.currentProjectId === "string" ? s.currentProjectId : undefined,
    currentProjectPlatform: typeof s.currentProjectPlatform === "string" ? (s.currentProjectPlatform as Platform) : undefined,
    plan: s.plan === "starter" || s.plan === "pro" ? s.plan : undefined,
    effectivePlan:
      s.effectivePlan === "starter" || s.effectivePlan === "pro"
        ? s.effectivePlan
        : undefined,
    isActive: typeof s.isActive === "boolean" ? s.isActive : undefined,
    isReadOnly: typeof s.isReadOnly === "boolean" ? s.isReadOnly : undefined,
    canUsePaidMessaging: typeof s.canUsePaidMessaging === "boolean" ? s.canUsePaidMessaging : undefined,
    status:
      s.status === "inactive" || s.status === "incomplete" || s.status === "incomplete_expired" || s.status === "trialing" || s.status === "active" || s.status === "past_due" || s.status === "canceled" || s.status === "unpaid" || s.status === "revoked" || s.status === "expired"
        ? s.status
        : undefined,
    accessLevel:
      s.accessLevel === "full" || s.accessLevel === "read_only" || s.accessLevel === "blocked"
        ? s.accessLevel
        : undefined,
    trialEndsAt: typeof s.trialEndsAt === "string" || s.trialEndsAt === null ? s.trialEndsAt : undefined,
    trialState:
      s.trialState === "active" || s.trialState === "expired" || s.trialState === "no_trial" || s.trialState === "none"
        ? s.trialState
        : undefined,
    onboardingComplete: typeof s.onboardingComplete === "boolean" ? s.onboardingComplete : undefined,
    onboardingStep: typeof s.onboardingStep === "number" ? s.onboardingStep : undefined,
  };
}

function readSessionFromStorage(): LocalSession | null {
  if (typeof window === "undefined") {
    return null;
  }

  try {
    const raw = window.localStorage.getItem(SESSION_KEY);
    if (!raw) {
      return null;
    }

    const parsed = sanitizeSession(JSON.parse(raw));
    if (!parsed) {
      window.localStorage.removeItem(SESSION_KEY);
      return null;
    }

    return parsed;
  } catch {
    return null;
  }
}

function writeSessionToStorage(session: LocalSession | null): void {
  if (typeof window === "undefined") {
    return;
  }

  try {
    if (!session) {
      window.localStorage.removeItem(SESSION_KEY);
      return;
    }

    window.localStorage.setItem(SESSION_KEY, JSON.stringify(session));
  } catch {
    // ignore storage failures
  }
}

export function getSession(): LocalSession | null {
  if (inMemorySession) {
    return inMemorySession;
  }

  const stored = readSessionFromStorage();
  if (stored) {
    inMemorySession = stored;
  }
  return stored;
}

export function saveSession(session: LocalSession): void {
  inMemorySession = session;
  writeSessionToStorage(session);
  emitSessionChanged();
}

export function clearSession(): void {
  inMemorySession = null;
  writeSessionToStorage(null);
  emitSessionChanged();
}

export function updateSession(
  partial: Partial<LocalSession>
): LocalSession | null {
  const current = getSession();
  if (!current) return null;

  const next = {
    ...current,
    ...partial
  };

  saveSession(next);
  return next;
}

export function isAuthed(): boolean {
  const session = getSession();
  return Boolean(session?.userId && session?.email);
}
